import { DataCard } from '@/reports/shared/DataCard/DataCard';
import { AutomobileAccident } from '@/reports/types/vehicles';
import {
  generateSectionIdentifyingData,
  generateSectionSourceData,
} from '@/reports/vehicle/VehicleReport';
import { Text, Accordion, styled, Table } from '@/theme/index';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { capitalizeString } from '@/utils/index';

interface VehicleAccident {
  accident: AutomobileAccident;
}

const StyledAccordion = styled(Accordion)(() => ({
  '.MuiAccordionSummary-content p': {
    fontSize: '20px',
  },
}));

export function VehicleAccidentCard({ accident }: VehicleAccident) {
  const accidentGeneralData = [
    ['Crash severity', capitalizeString(accident.crash_severity)],
    ['Manner of collision', capitalizeString(accident.manner_of_collision)],
    ['Impact area', capitalizeString(accident.impact_area)],
  ].filter(([, value]) => Boolean(value));

  const accidentDetailsData = [
    ['Report ID', accident.report_id || ''],
    ['Reporting agency', capitalizeString(accident.reporting_agency || '')],
    ['Type', capitalizeString(accident.type || '')],
    ['Damage severity', capitalizeString(accident.damage_severity || '')],
    ['Object struck', capitalizeString(accident.object_struck || '')],
    ['Airbags deployed', accident.airbags_deployed ? 'Yes' : ''],
    ['Estimated speed', accident.estimated_speed || ''],
    ['Posted speed', accident.posted_speed || ''],
    ['Crash location', accident.crash_location || ''],
    ['License state', accident.license_state || ''],
    ['Insurance company', capitalizeString(accident.insurance_company || '')],
  ].filter(([, value]) => Boolean(value));

  const accidentIdentifyingData = generateSectionIdentifyingData(accident);
  const accidentSourceData = generateSectionSourceData(accident);

  return (
    <DataCard testId="vehicle-accidents-card" id={accident.id || ''}>
      <Text
        variant="h3"
        fontWeight="normal"
        data-testid="vehicle-accidents-card-title"
      >
        Accident reported
        {accident.date?.full && (
          <>
            <Text variant="h3" fontWeight="normal" component="span">
              {' '}
              on
            </Text>
            <Text variant="h3" component="span">
              {' '}
              {formatDateFromReport(accident.date?.full)}
            </Text>
          </>
        )}
        {Boolean(accident.crash_location) && (
          <>
            <Text variant="h3" fontWeight="normal" component="span">
              {' '}
              in
            </Text>
            <Text variant="h3" component="span">
              {' '}
              {accident.crash_location}
            </Text>
          </>
        )}
      </Text>
      <Table
        rows={accidentGeneralData}
        data-testid="vehicle-accidents-card-general"
        className="flipped"
      />
      <Text variant="h4">Accident information</Text>
      <Table className="striped flipped" rows={accidentDetailsData} />
      {accidentSourceData.length > 0 && (
        <StyledAccordion display="Source information">
          <Table
            className="striped flipped"
            rows={accidentSourceData}
            sx={{ wordBreak: 'break-word' }}
          />
        </StyledAccordion>
      )}
      {accidentIdentifyingData.length > 0 && (
        <StyledAccordion display="Identifying information">
          <Table className="striped flipped" rows={accidentIdentifyingData} />
        </StyledAccordion>
      )}
    </DataCard>
  );
}
